import { LeftOutlined } from '@ant-design/icons';
import loginBg from 'assets/login-bg.jpg';
import IconRound from 'assets/logos/icon-round.svg?react';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { cn } from 'lib/utils';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';

export type AuthContainerProps = {
  title: string;
  description: string;
  mode: 'register' | 'login' | 'passwordReset';
  bottomLinkTo: 'register' | 'login' | 'none';
  displayLogo?: boolean;
  displayBackButton?: boolean;
  onBackButtonClick?: () => void;
};

export default function AuthContainer(props: PropsWithChildren<AuthContainerProps>) {
  const {
    title,
    description,
    mode,
    children,
    bottomLinkTo,
    displayLogo = false,
    displayBackButton = false,
    onBackButtonClick,
  } = props;

  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section
      className={cn(
        'flex flex-row bg-card',
        mode === 'register' ? 'items-start' : 'items-center',
        mode !== 'register' && 'md:h-dvh',
      )}
    >
      <div className="hidden h-full w-1/2 max-w-1/2 shrink-0 overflow-hidden md:flex">
        <img alt="login-bg" src={loginBg} className="h-auto w-full" />
      </div>
      <div className="mx-auto flex shrink-0 grow-0 basis-4/5 flex-col gap-6 px-4 py-12 md:basis-2/5 md:p-8">
        {displayBackButton && (
          <Button variant="link" className="self-start" onClick={onBackButtonClick ?? (() => navigate(-1))}>
            <LeftOutlined />
            <span className="text-muted-foreground">{t('loginRegister.actions.back')}</span>
          </Button>
        )}
        {displayLogo && (
          <div className="mt-32 flex items-center justify-center text-foreground">
            <IconRound fill="currentColor" height={128} />
          </div>
        )}
        <div>
          <h1 className="text-2xl mb-2">{title}</h1>
          <span className="whitespace-pre-line text-muted-foreground">{description}</span>
        </div>
        {children}
        <div>
          {bottomLinkTo !== 'none' && (
            <Link to={bottomLinkTo === 'register' ? routes.register.route : routes.login.route} className="underline">
              {bottomLinkTo === 'register'
                ? t('loginRegister.actions.noAccount')
                : t('loginRegister.actions.alreadyRegistered')}
            </Link>
          )}
        </div>
        <Separator />
      </div>
    </section>
  );
}
